import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2/dist/sweetalert2.js";
import FinanceV2Layout from "./FinanceV2Layout";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import { downloadCsv, datePresets } from "./financeV2Export";
import "./FinanceV2.css";

const defaultRange = datePresets.mtd();

export default function FinanceV2Journals() {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [sourceFilter, setSourceFilter] = useState("");
  const [journals, setJournals] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({
    entry_date: new Date().toISOString().split("T")[0],
    description: "",
    lines: [
      { account_id: "", debit: "", credit: "", memo: "" },
      { account_id: "", debit: "", credit: "", memo: "" },
    ],
  });

  const canManage = [1, 3, 5].includes(parseInt(role, 10));

  const load = () => {
    const params = { branch_id, per_page: 100, from, to };
    if (sourceFilter) params.source_type = sourceFilter;
    FinanceV2Services.getJournals(params).then((res) => {
      setJournals(res.data?.data?.data || res.data?.data || []);
    });
  };

  useEffect(() => {
    load();
    FinanceV2Services.getAccountsList(branch_id).then((res) => {
      setAccounts(res.data?.data || []);
    });
  }, [branch_id, from, to, sourceFilter]);

  const exportCsv = () => {
    downloadCsv(
      `journals-${from}-${to}.csv`,
      ["Date", "Reference", "Source", "Description", "Debit", "Credit", "Status"],
      journals.map((j) => [
        j.entry_date,
        j.reference,
        j.source_type,
        j.description,
        j.total_debit,
        j.total_credit,
        j.status,
      ])
    );
  };

  const submitJournal = async () => {
    try {
      await FinanceV2Services.createJournal({
        ...form,
        branch_id,
        lines: form.lines.map((l) => ({
          account_id: parseInt(l.account_id, 10),
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          memo: l.memo,
        })),
      });
      toast.success("Journal posted");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Journal must balance (debits = credits)");
    }
  };

  const voidJournal = async (journal) => {
    if (!canManage || journal.status === "void") return;
    const result = await Swal.fire({
      title: "Void this journal?",
      text: `${journal.reference || "#" + journal.id} will be excluded from reports.`,
      input: "text",
      inputPlaceholder: "Reason (optional)",
      showCancelButton: true,
      confirmButtonText: "Void",
      confirmButtonColor: "#c0392b",
    });
    if (!result.isConfirmed) return;
    try {
      await FinanceV2Services.voidJournal(journal.id, {
        reason: result.value || "Voided by finance user",
        branch_id,
      });
      toast.success("Journal voided");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not void journal");
    }
  };

  return (
    <FinanceV2Layout>
      <div className="container-fluid px-0 py-2">
        <div className="d-flex justify-content-between mb-3">
          <h4>Journal entries</h4>
          <div>
            <button type="button" className="btn btn-sm btn-success me-2" onClick={() => setShowForm(!showForm)}>
              {showForm ? "Cancel" : "New manual entry"}
            </button>
            <Link to="/finance-v2/dashboard" className="btn btn-sm btn-outline-secondary">
              Back
            </Link>
          </div>
        </div>

        <div className="fv2-card mb-3">
          <div className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label small">From</label>
              <input type="date" className="form-control form-control-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="col-auto">
              <label className="form-label small">To</label>
              <input type="date" className="form-control form-control-sm" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="col-auto">
              <label className="form-label small">Source</label>
              <select className="form-select form-select-sm" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                <option value="">All</option>
                <option value="sales_order">Sales</option>
                <option value="expense_legacy">Legacy expense</option>
                <option value="expense">Expense</option>
                <option value="payroll">Payroll</option>
                <option value="charity">Charity</option>
                <option value="bank_ledger">Bank ledger</option>
                <option value="petty_cash">Petty cash</option>
                <option value="liability_ledger">Liability ledger</option>
                <option value="manual">Manual</option>
                <option value="opening_balance">Opening balance</option>
              </select>
            </div>
            <div className="col-auto">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load}>
                Refresh
              </button>
            </div>
            <div className="col-auto">
              <button type="button" className="btn btn-sm btn-outline-dark" onClick={exportCsv}>
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {showForm && (
          <div className="fv2-card mb-3">
            <input
              type="date"
              className="form-control form-control-sm mb-2 w-auto"
              value={form.entry_date}
              onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
            />
            <input
              className="form-control form-control-sm mb-2"
              placeholder="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            {form.lines.map((line, i) => (
              <div className="row g-2 mb-2" key={i}>
                <div className="col-md-5">
                  <select
                    className="form-select form-select-sm"
                    value={line.account_id}
                    onChange={(e) => {
                      const lines = [...form.lines];
                      lines[i].account_id = e.target.value;
                      setForm({ ...form, lines });
                    }}
                  >
                    <option value="">Account</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Debit"
                    value={line.debit}
                    onChange={(e) => {
                      const lines = [...form.lines];
                      lines[i].debit = e.target.value;
                      setForm({ ...form, lines });
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Credit"
                    value={line.credit}
                    onChange={(e) => {
                      const lines = [...form.lines];
                      lines[i].credit = e.target.value;
                      setForm({ ...form, lines });
                    }}
                  />
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-sm btn-outline-secondary me-2" onClick={() => setForm({ ...form, lines: [...form.lines, { account_id: "", debit: "", credit: "", memo: "" }] })}>
              Add line
            </button>
            <button type="button" className="btn btn-sm btn-success" onClick={submitJournal}>
              Post journal
            </button>
          </div>
        )}

        <div className="fv2-card fv2-table-wrap">
          <table className="table table-sm">
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Ref</th>
                <th>Source</th>
                <th>Description</th>
                <th>Status</th>
                <th className="text-end">Debit</th>
                <th className="text-end">Credit</th>
                {canManage ? <th className="text-end">Action</th> : null}
              </tr>
            </thead>
            <tbody>
              {journals.map((j) => (
                <React.Fragment key={j.id}>
                  <tr className={j.status === "void" ? "table-secondary" : ""}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0"
                        onClick={() => setExpandedId(expandedId === j.id ? null : j.id)}
                      >
                        {expandedId === j.id ? "−" : "+"}
                      </button>
                    </td>
                    <td>{j.entry_date}</td>
                    <td>{j.reference}</td>
                    <td>
                      <span className="badge bg-secondary">{j.source_type}</span>
                    </td>
                    <td>{j.description}</td>
                    <td>
                      <span className={`badge ${j.status === "void" ? "bg-danger" : "bg-success"}`}>
                        {j.status}
                      </span>
                    </td>
                    <td className="text-end">{j.total_debit}</td>
                    <td className="text-end">{j.total_credit}</td>
                    {canManage ? (
                      <td className="text-end">
                        {j.status !== "void" ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => voidJournal(j)}
                          >
                            Void
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    ) : null}
                  </tr>
                  {expandedId === j.id && (
                    <tr>
                      <td colSpan={canManage ? 9 : 8}>
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr>
                              <th>Account</th>
                              <th>Memo</th>
                              <th className="text-end">Debit</th>
                              <th className="text-end">Credit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(j.lines || []).map((line) => (
                              <tr key={line.id}>
                                <td>
                                  {line.account?.code} {line.account?.name}
                                </td>
                                <td>{line.memo}</td>
                                <td className="text-end">{line.debit}</td>
                                <td className="text-end">{line.credit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </FinanceV2Layout>
  );
}
